/** @jsxImportSource @emotion/react */

import { FC, useCallback, useMemo } from 'react'
import { useForm } from 'react-final-form'
import Button from '@mui/material/Button'
import TableButtons from 'components/TableButtons/TableButtons'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import * as tableClasses from '../styles'
import { SimpleEndodonticTableData, SimpleEndodonticTableRow } from './types'
import {
  MAX_SIMPLE_ENDODONTIC_ROWS,
  MIN_SIMPLE_ENDODONTIC_ROWS,
  SIMPLE_ENDODONTIC_TABLE_COLUMNS,
  createEmptySimpleEndodonticTable,
  createEmptySimpleEndodonticTableRow,
  parseSimpleEndodonticTable,
  stringifySimpleEndodonticTable,
} from './helpers'
import * as classes from './styles'

type Props = {
  name: string
  value?: string | null
}

export const SimpleEndodonticTable: FC<Props> = ({ name, value }) => {
  const form = useForm()
  const tableData = useMemo(() => parseSimpleEndodonticTable(value) ?? createEmptySimpleEndodonticTable(), [value])

  const updateTable = useCallback(
    (updater: (data: SimpleEndodonticTableData) => SimpleEndodonticTableData) => {
      form.change(name, stringifySimpleEndodonticTable(updater(tableData)))
    },
    [form, name, tableData]
  )

  const updateCell = useCallback(
    (rowIndex: number, key: keyof SimpleEndodonticTableRow, cellValue: string) => {
      updateTable((data) => ({
        ...data,
        rows: data.rows.map((row, index) => (index === rowIndex ? { ...row, [key]: cellValue } : row)),
      }))
    },
    [updateTable]
  )

  const addRow = useCallback(() => {
    updateTable((data) => {
      if (data.rows.length >= MAX_SIMPLE_ENDODONTIC_ROWS) {
        return data
      }

      return {
        ...data,
        rows: [...data.rows, createEmptySimpleEndodonticTableRow()],
      }
    })
  }, [updateTable])

  const removeRow = useCallback(
    (rowIndex: number) => {
      updateTable((data) => {
        if (data.rows.length <= MIN_SIMPLE_ENDODONTIC_ROWS) {
          return data
        }

        return {
          ...data,
          rows: data.rows.filter((_, index) => index !== rowIndex),
        }
      })
    },
    [updateTable]
  )

  const canRemoveRows = tableData.rows.length > MIN_SIMPLE_ENDODONTIC_ROWS

  return (
    <div>
      <div css={tableClasses.tableContainer(0)}>
        <table css={tableClasses.table}>
          <thead>
            <tr>
              {SIMPLE_ENDODONTIC_TABLE_COLUMNS.map(({ key, label }) => (
                <th key={key} css={tableClasses.fixedCell}>
                  {label}
                </th>
              ))}
              <th css={classes.actionsHeader} />
            </tr>
          </thead>
          <tbody>
            {tableData.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {SIMPLE_ENDODONTIC_TABLE_COLUMNS.map(({ key }) => (
                  <td key={key}>
                    <input
                      type="text"
                      value={row[key]}
                      onChange={(event) => updateCell(rowIndex, key, event.target.value)}
                      css={tableClasses.nativeInput}
                    />
                  </td>
                ))}
                <td css={classes.actionsCell}>
                  <Button
                    variant="outlined"
                    color="red"
                    size="small"
                    onClick={() => removeRow(rowIndex)}
                    disabled={!canRemoveRows}
                    css={classes.removeButton}
                  >
                    <TrashIcon />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TableButtons
        itemName="Tooth"
        onAddItem={addRow}
        disabled={tableData.rows.length >= MAX_SIMPLE_ENDODONTIC_ROWS}
      />
    </div>
  )
}
